import { Button, Modal } from "react-bootstrap";
import type { AdLock } from "@models/Ad.model";

type AdLockedDialogProps = {
    show: boolean;
    adName?: string;
    lock: AdLock | null;
    /** True when opened from an ad group — a copy replaces the ad there, so the copy IS
     *  what goes live in this engagement. Changes what the copy actually does. */
    replacesInAdGroup: boolean;
    /** Omit where copying isn't offered. Kinema can't: the fork API returns the copy's
     *  root, not the counterpart of the leaf being edited, so there is nowhere to land. */
    onCopy?: () => void;
    onEditAnyway: () => void;
    onClose: () => void;
    isWorking?: boolean;
    error?: string | null;
};

/** Why this ad can't be freely edited, in the user's terms rather than the model's. */
function reason(lock: AdLock | null): string {
    if (lock?.is_approved && lock?.is_published) {
        return "was approved and is live on a network";
    }
    if (lock?.is_published) {
        return "is live on a network";
    }
    return "was approved and is in the client's catalog";
}

const TITLE = "Editing approved content";

/**
 * Shown when someone tries to edit content on an approved or published ad. The server
 * refuses those edits (409 `content_locked`) unless the request carries `force_edit`, so
 * this dialog is what turns that flag on — the confirmation lives where the mutation does.
 *
 * "Edit anyway" is deliberately not the primary action: it rewrites content a reviewer
 * signed off on, and on a published ad it changes what the next publish pushes to the
 * network, blending old and new performance onto one deployment.
 */
export default function AdLockedDialog({
    show,
    adName,
    lock,
    replacesInAdGroup,
    onCopy,
    onEditAnyway,
    onClose,
    isWorking = false,
    error = null,
}: AdLockedDialogProps) {
    const name = adName ? `"${adName}"` : "This ad";

    return (
        <Modal
            centered
            show={show}
            onHide={isWorking ? undefined : onClose}
            backdrop={isWorking ? "static" : true}
            keyboard={!isWorking}
            className="ceh-nested-modal"
            backdropClassName="ceh-nested-backdrop"
            style={{ zIndex: 1400 }}
        >
            <Modal.Header closeButton={!isWorking}>
                <Modal.Title>{TITLE}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-1">
                    {name} {reason(lock)}.
                    {lock?.is_published ? " Your changes go live on the next publish." : ""}
                </p>
                <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
                    {!onCopy
                        ? "To keep the approved version, copy it from the ad editor."
                        : replacesInAdGroup
                            ? "A copy replaces it in this ad group. Other engagements keep the original."
                            : "A copy lands in the catalog. Every engagement keeps the original."}
                </p>
                {error && (
                    <p className="mt-2 mb-0 text-danger" style={{ fontSize: 13 }}>
                        {error}
                    </p>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose} disabled={isWorking}>
                    Cancel
                </Button>
                <Button
                    variant={onCopy ? "outline-danger" : "danger"}
                    onClick={onEditAnyway}
                    disabled={isWorking}
                >
                    Edit anyway
                </Button>
                {onCopy && (
                    <Button variant="primary" onClick={onCopy} disabled={isWorking}>
                        {isWorking ? "Making a copy..." : "Make a copy & edit"}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}
